// Creator Studio: a YouTube-Studio-style dashboard where a publisher monitors and
// manages their own streams. Lists only the caller's streams (GET /api/me/streams);
// gated behind a publish capability.
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Clapperboard, Eye, Radio, Settings2, Video } from "lucide-react";
import { useMe, useMyStreams } from "../api/hooks.js";
import { hasPublish, type MyStreamDTO, type StreamStatus } from "../api/types.js";
import { useLoginModal } from "../ui/loginModal.js";
import { pageVariants, staggerContainer, riseItem } from "@/lib/motion";
import { Button } from "../components/ui/button.js";
import { StatusBadge } from "../components/ui/LiveBadge.js";
import { Skeleton } from "../components/ui/skeleton.js";
import { formatCount, relativeTime } from "../components/ui/format.js";
import { CreateStreamModal } from "../components/CreateStreamModal.js";

const STATUS_RANK: Record<StreamStatus, number> = {
  live: 0,
  paused: 1,
  scheduled: 2,
  waiting: 3,
  offline: 4,
  ended: 5,
};

function StreamRow({ s, onManage }: { s: MyStreamDTO; onManage: () => void }) {
  const { t } = useTranslation();
  return (
    <motion.div
      variants={riseItem}
      className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-white/[0.06] bg-card/50 p-3.5 backdrop-blur-sm transition-colors hover:border-white/[0.12] sm:flex-nowrap"
    >
      <div className="w-20 shrink-0">
        <StatusBadge status={s.status} />
      </div>

      <div className="min-w-0 flex-1">
        <Link
          to={`/watch/${s.slug ?? s.uuid}`}
          className="block truncate text-sm font-medium text-foreground outline-none hover:text-primary focus-visible:text-primary"
        >
          {s.title}
        </Link>
        <p className="truncate text-xs text-muted-foreground/70">
          {t("studio.ago", { time: relativeTime(s.startedAt ?? s.createdAt) })}
          {s.region ? ` · ${s.region}` : ""}
          {s.featured ? ` · ${t("studio.featured")}` : ""}
        </p>
      </div>

      <div className="flex w-24 shrink-0 items-center gap-1.5 text-sm tabular-nums text-muted-foreground">
        {s.status === "live" ? (
          <>
            <Eye className="h-4 w-4 text-destructive-soft" />
            {formatCount(s.viewerCount)}
          </>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        )}
      </div>

      <div className="flex w-20 shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
        {s.recordingCount > 0 ? (
          <>
            <Video className="h-4 w-4" />
            {s.recordingCount}
          </>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        )}
      </div>

      <Button variant="secondary" size="sm" className="shrink-0" onClick={onManage}>
        <Settings2 className="h-4 w-4" />
        {t("studio.manage")}
      </Button>
    </motion.div>
  );
}

export function StudioPage() {
  const { t } = useTranslation();
  const { data: me, isLoading: meLoading } = useMe();
  const { openLogin } = useLoginModal();
  const navigate = useNavigate();
  const q = useMyStreams();
  const [createOpen, setCreateOpen] = useState(false);

  const items = q.data?.items ?? [];

  // A single list, sorted live-first. No filter tabs — an individual streamer has
  // few streams, so a sorted list is enough.
  const sorted = useMemo(
    () => items.slice().sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status]),
    [items],
  );

  // ── Gating ──
  if (!meLoading && !me) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-20 text-center">
        <h1 className="font-display text-xl font-semibold text-foreground">{t("studio.loginTitle")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("studio.loginHint")}</p>
        <Button variant="primary" className="mt-4" onClick={openLogin}>
          {t("nav.login")}
        </Button>
      </div>
    );
  }

  if (me && !hasPublish(me.capabilities)) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-20 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/[0.06] text-muted-foreground">
          <Clapperboard className="h-6 w-6" />
        </div>
        <h1 className="font-display text-xl font-semibold text-foreground">{t("studio.noPublishTitle")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("studio.noPublishHint")}</p>
      </div>
    );
  }

  return (
    <motion.main
      variants={pageVariants}
      initial="initial"
      animate="enter"
      className="mx-auto max-w-6xl px-4 py-8 sm:py-10"
    >
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            {t("studio.title")}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{t("studio.subtitle")}</p>
        </div>
        <Button variant="primary" onClick={() => setCreateOpen(true)}>
          <Radio className="h-4 w-4" />
          {t("studio.goLive")}
        </Button>
      </div>

      {/* My streams (per-stream stats live in each stream's manage view) */}
      <div>
        <h2 className="mb-4 font-display text-lg font-semibold text-foreground">{t("studio.myStreams")}</h2>

        {q.isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-xl" />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="surface flex flex-col items-center px-6 py-16 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Radio className="h-6 w-6" />
            </div>
            <p className="text-lg font-medium text-foreground">{t("studio.emptyAll")}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t("studio.emptyHint")}</p>
          </div>
        ) : (
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="enter"
            className="space-y-3"
          >
            {sorted.map((s) => (
              <StreamRow key={s.uuid} s={s} onManage={() => navigate(`/studio/${s.uuid}`)} />
            ))}
          </motion.div>
        )}
      </div>

      <CreateStreamModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </motion.main>
  );
}
